import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import config from 'config';
import formatter from 'common/helpers/format/formatter';

import { NumberControl, AmountControl } from 'components/Controls';

import { CELL_TYPES, CELL_TYPES_ARRAY } from '../../enums';
import { useLineItemSelector, useLineItemField } from '../../context';

import styles from './cell.module.css';

const CLASSES = {
  [CELL_TYPES.amount]: styles.amount,
  [CELL_TYPES.number]: styles.number,
  [CELL_TYPES.label]: styles.label,
  [CELL_TYPES.currency]: styles.currency,
  [CELL_TYPES.percentage]: styles.percentage,
  [CELL_TYPES.empty]: styles.empty,
};

const CellContent = ({
  id,
  type,
  alias,
  max,
  locked,
  disabled,
  focused,
  allowBlank,
  hasError,
  ariaLabel,
  pdf,
  title,
}) => {
  const value = useLineItemField(alias);
  const maxValue = useLineItemSelector(lineItem => (typeof max === 'string' ? lineItem[max] : max));

  const onChange = useLineItemField('onChange');
  const handleChange = useCallback(nextValue => onChange?.(alias, nextValue), [alias, onChange]);

  switch (type) {
    case CELL_TYPES.amount:
      return (
        <AmountControl
          id={id}
          value={value}
          lockedState={locked}
          disabledState={disabled}
          focus={focused}
          allowBlankField={allowBlank}
          ariaLabel={ariaLabel}
          min={0}
          max={maxValue}
          className={cn({ [styles.error]: hasError })}
          onChange={handleChange}
        />
      );

    case CELL_TYPES.number:
      return (
        <NumberControl
          id={id}
          value={value}
          lockedState={locked}
          disabledState={disabled}
          focus={focused}
          allowBlankField={allowBlank}
          ariaLabel={ariaLabel}
          min={0}
          max={maxValue}
          className={cn({ [styles.error]: hasError })}
          onChange={handleChange}
        />
      );

    case CELL_TYPES.label:
      return !pdf ? (
        <span title={title}>{value}</span>
      ) : (
        <a target="_blank" className={styles.link} rel="noopener noreferrer" href={`${config.api.url}/${pdf}`}>
          {value}
        </a>
      );

    case CELL_TYPES.currency:
      return formatter(value);

    case CELL_TYPES.percentage:
      return `${Math.round(value)}%`;

    case CELL_TYPES.empty:
      return '\u2013';

    default:
      return null;
  }
};

const Cell = ({ className, onMounted, ...props }) => {
  const { type, disabled, focused } = props;

  useEffect(() => void (focused && onMounted?.()), [focused, onMounted]);

  return (
    <td className={cn(styles.td, CLASSES[type], { [styles.disabled]: disabled }, className)}>
      <CellContent {...props} />
    </td>
  );
};

Cell.propTypes = {
  id: PropTypes.string,
  type: PropTypes.oneOf(CELL_TYPES_ARRAY),
  alias: PropTypes.string,
  max: PropTypes.number,
  title: PropTypes.string,
  locked: PropTypes.bool,
  disabled: PropTypes.bool,
  focused: PropTypes.bool,
  allowBlank: PropTypes.bool,
  hasError: PropTypes.bool,
  ariaLabel: PropTypes.string,
  pdf: PropTypes.string,
  className: PropTypes.string,
  onMounted: PropTypes.func,
};

Cell.defaultProps = {
  type: CELL_TYPES.label,
  locked: false,
  disabled: false,
  focused: false,
  allowBlank: false,
  hasError: false,
};

export default Cell;
